/**
 * Validation utilities for Static Web Generator
 * Combines schema validation and UI validation helpers
 */

import {
  FieldSchema,
  FieldSchemaValue,
  ValidationDict,
} from "../../../types/staticWebGenerator";
import {
  schemaPathToComponentField,
  getComponentSectionFromSchemaId,
  SCHEMA_TO_COMPONENT_MAPPING,
} from "./fieldMapping";

// ============================================================================
// SCHEMA VALIDATION
// ============================================================================

export interface TabConfig {
  id: string;
  label: string;
  required: boolean;
  fields: FieldConfig[];
}

export interface FieldConfig {
  path: string;
  label: string;
  required: boolean;
  type?: string;
  value: any;
  description?: string;
  minimum_items?: number;
  maximum_items?: number;
}

/**
 * Recursively parse field schema to extract all fields with their paths
 */
export const parseSchemaFields = (
  schema: FieldSchema,
  prefix = "",
): FieldConfig[] => {
  const fields: FieldConfig[] = [];

  for (const [key, value] of Object.entries(schema)) {
    const path = prefix ? `${prefix}.${key}` : key;

    if (isFieldSchemaValue(value)) {
      // This is a leaf field
      fields.push({
        path,
        label: formatFieldLabel(key),
        required: value.required,
        type: value.type,
        value: value.value,
        description: value.description,
        minimum_items: value.minimum_items,
        maximum_items: value.maximum_items,
      });
    } else {
      // This is a nested object, recurse
      fields.push(...parseSchemaFields(value as FieldSchema, path));
    }
  }

  return fields;
};

/**
 * Check if a value is a FieldSchemaValue (leaf node)
 */
const isFieldSchemaValue = (
  value: FieldSchemaValue | FieldSchema,
): value is FieldSchemaValue => {
  return (
    typeof value === "object" &&
    value !== null &&
    "required" in value &&
    "value" in value
  );
};

/**
 * Format field key into a readable label
 */
const formatFieldLabel = (key: string): string => {
  return key
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

/**
 * Group fields by their top-level section (e.g., "hero_section", "color")
 */
export const groupFieldsBySection = (
  fields: FieldConfig[],
): Map<string, FieldConfig[]> => {
  const sections = new Map<string, FieldConfig[]>();

  for (const field of fields) {
    const sectionKey = field.path.split(".")[0];
    if (!sections.has(sectionKey)) {
      sections.set(sectionKey, []);
    }
    sections.get(sectionKey)!.push(field);
  }

  return sections;
};

/**
 * Create tab configurations from schema
 * Required fields go in "Required" tab, optional in "Optional" tab
 */
export const createTabsFromSchema = (
  schema: FieldSchema | null,
): TabConfig[] => {
  if (!schema) {
    return [];
  }

  const fields = parseSchemaFields(schema);
  const sections = groupFieldsBySection(fields);

  const requiredTabs: TabConfig[] = [];
  const optionalTabs: TabConfig[] = [];

  // Map section keys to human-readable tab names
  const sectionLabels: Record<string, string> = {
    color: "Colors",
    font: "Fonts",
    hero_section: "Hero Section",
    our_services: "Services",
    service_highlights: "Service Highlights",
    team: "Team",
    company_name: "Company Name",
    testimonials: "Testimonials",
    gallery: "Gallery",
    socials: "Social Media",
    default_section: "Default Content",
    google_maps: "Google Maps",
  };

  for (const [sectionKey, sectionFields] of Array.from(sections.entries())) {
    const hasRequiredFields = sectionFields.some((f) => f.required);
    const tabLabel = sectionLabels[sectionKey] || formatFieldLabel(sectionKey);

    const tab: TabConfig = {
      id: sectionKey,
      label: tabLabel,
      required: hasRequiredFields,
      fields: sectionFields,
    };

    if (hasRequiredFields) {
      requiredTabs.push(tab);
    } else {
      optionalTabs.push(tab);
    }
  }

  // Sort required tabs first, then optional
  return [...requiredTabs, ...optionalTabs];
};

/**
 * Get only required tabs from schema
 */
export const getRequiredTabsFromSchema = (
  schema: FieldSchema | null,
): TabConfig[] => {
  if (!schema) {
    return [];
  }

  const fields = parseSchemaFields(schema);
  const sections = groupFieldsBySection(fields);

  const requiredTabs: TabConfig[] = [];

  // Map section keys to human-readable tab names
  const sectionLabels: Record<string, string> = {
    color: "Colors",
    font: "Fonts",
    hero_section: "Hero Section",
    our_services: "Services",
    service_highlights: "Service Highlights",
    team: "Team",
    company_name: "Company Name",
    testimonials: "Testimonials",
    gallery: "Gallery",
    socials: "Social Media",
    default_section: "Default Content",
    google_maps: "Google Maps",
  };

  for (const [sectionKey, sectionFields] of Array.from(sections.entries())) {
    const hasRequiredFields = sectionFields.some((f) => f.required);
    if (hasRequiredFields) {
      const tabLabel =
        sectionLabels[sectionKey] || formatFieldLabel(sectionKey);
      requiredTabs.push({
        id: sectionKey,
        label: tabLabel,
        required: true,
        fields: sectionFields,
      });
    }
  }

  return requiredTabs;
};

/**
 * Get only optional tabs from schema
 */
export const getOptionalTabsFromSchema = (
  schema: FieldSchema | null,
): TabConfig[] => {
  if (!schema) {
    return [];
  }

  const fields = parseSchemaFields(schema);
  const sections = groupFieldsBySection(fields);

  const optionalTabs: TabConfig[] = [];

  // Map section keys to human-readable tab names
  const sectionLabels: Record<string, string> = {
    color: "Colors",
    font: "Fonts",
    hero_section: "Hero Section",
    our_services: "Services",
    service_highlights: "Service Highlights",
    team: "Team",
    company_name: "Company Name",
    testimonials: "Testimonials",
    gallery: "Gallery",
    socials: "Social Media",
    default_section: "Default Content",
    google_maps: "Google Maps",
  };

  for (const [sectionKey, sectionFields] of Array.from(sections.entries())) {
    const hasRequiredFields = sectionFields.some((f) => f.required);
    if (!hasRequiredFields) {
      const tabLabel =
        sectionLabels[sectionKey] || formatFieldLabel(sectionKey);
      optionalTabs.push({
        id: sectionKey,
        label: tabLabel,
        required: false,
        fields: sectionFields,
      });
    }
  }

  return optionalTabs;
};

/**
 * Get nested value from object using dot notation path
 */
const getNestedValue = (obj: any, path: string): any => {
  return path.split(".").reduce((current, key) => current?.[key], obj);
};

/**
 * Check if value is empty
 */
const isEmpty = (value: any): boolean => {
  if (value === undefined || value === null) return true;
  if (typeof value === "string" && value.trim() === "") return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
};

/**
 * Recursively validate nested fields based on schema
 */
function validateNestedFields(
  schemaValue: FieldSchema | FieldSchemaValue,
  pathPrefix: string,
  dataValue: any,
  missingFields: string[],
  missingValues: string[],
  emptyValues: string[],
): void {
  if (isFieldSchemaValue(schemaValue)) {
    // This is a leaf field
    const fieldPath = pathPrefix;
    const isRequired = schemaValue.required;
    const hasMinimumItems = schemaValue.minimum_items !== undefined;

    // Get value from data
    // dataValue is already the value for this field (passed from parent recursion)
    const value = dataValue;

    // Check if required field is missing/empty
    if (isRequired) {
      const isMissing = value === undefined || value === null;
      const isEmptyString = typeof value === "string" && value.trim() === "";
      const isEmptyArray = Array.isArray(value) && value.length === 0;
      const isEmpty = value === "" || isEmptyString || isEmptyArray;

      if (isMissing || isEmpty) {
        missingFields.push(fieldPath);
        if (isMissing) {
          missingValues.push(fieldPath);
        } else if (isEmpty) {
          emptyValues.push(fieldPath);
        }
      }
    }

    // Check array minimum_items constraint
    if (hasMinimumItems && Array.isArray(value)) {
      const itemCount = value.length;
      const minimum = schemaValue.minimum_items!;

      if (itemCount < minimum) {
        missingFields.push(fieldPath);
        emptyValues.push(fieldPath);
      }
    }
  } else {
    // This is a nested object - recurse
    const nestedSchema = schemaValue as FieldSchema;

    for (const [key, value] of Object.entries(nestedSchema)) {
      const nestedPath = pathPrefix ? `${pathPrefix}.${key}` : key;
      // Get the nested data value for this key
      const nestedDataValue = dataValue?.[key];
      validateNestedFields(
        value as FieldSchema | FieldSchemaValue,
        nestedPath,
        nestedDataValue,
        missingFields,
        missingValues,
        emptyValues,
      );
    }
  }
}

/**
 * Validate that all required fields have values
 * 100% schema-driven validation - no hardcoded rules
 * Returns separate lists for missing values and empty string values
 */
export const validateRequiredFields = (
  data: any,
  schema: FieldSchema | null,
  optionalSectionStates?: Record<string, boolean>,
): {
  isValid: boolean;
  missingFields: string[];
  missingValues: string[]; // Fields that are undefined/null
  emptyValues: string[]; // Fields that are empty strings/whitespace/empty arrays
} => {
  if (!schema) {
    return {
      isValid: true,
      missingFields: [],
      missingValues: [],
      emptyValues: [],
    };
  }

  const missingFields: string[] = [];
  const missingValues: string[] = [];
  const emptyValues: string[] = [];

  /**
   * Optional sections are controlled by UI toggles.
   * If a section is disabled, we should skip validating it entirely.
   *
   * `optionalSectionStates` uses component-centric keys.
   */
  const shouldValidateSchemaSection = (schemaSectionKey: string): boolean => {
    if (!optionalSectionStates) return true;

    const toggleKey =
      schemaSectionKey === "service_highlights"
        ? "highlights"
        : schemaSectionKey === "announcement_modal"
          ? "announcementBanner"
          : schemaSectionKey;

    // Optional content sections
    if (
      toggleKey === "gallery" ||
      toggleKey === "testimonials" ||
      toggleKey === "announcementBanner" ||
      toggleKey === "banner" ||
      toggleKey === "highlights" ||
      toggleKey === "team"
    ) {
      return optionalSectionStates[toggleKey] === true;
    }

    // Social links are only relevant when icons are shown
    if (schemaSectionKey === "socials") {
      return optionalSectionStates.showSocialIcons === true;
    }

    return true;
  };

  // Validate all top-level sections
  for (const [sectionKey, sectionSchema] of Object.entries(schema)) {
    if (!shouldValidateSchemaSection(sectionKey)) {
      continue;
    }

    /**
     * Schema supports two shapes:
     * 1) Plain nested objects
     * 2) Wrapped sections: { required, value: { ...nested schema... } }
     */
    if (isFieldSchemaValue(sectionSchema)) {
      const wrapped = sectionSchema as FieldSchemaValue;
      const maybeNested = wrapped.value;

      // Wrapped section: validate nested fields against data[sectionKey]
      if (
        maybeNested &&
        typeof maybeNested === "object" &&
        !Array.isArray(maybeNested)
      ) {
        // If section is required and missing entirely, report it as missing
        const sectionExistsInData =
          data !== null && data !== undefined && data[sectionKey] !== undefined;
        if (wrapped.required && !sectionExistsInData) {
          missingFields.push(sectionKey);
          missingValues.push(sectionKey);
          continue;
        }

        validateNestedFields(
          maybeNested as FieldSchema,
          sectionKey,
          data?.[sectionKey],
          missingFields,
          missingValues,
          emptyValues,
        );
        continue;
      }

      // True top-level leaf field (non-object): validate directly
      if (wrapped.required) {
        const value = data?.[sectionKey];
        if (isEmpty(value)) {
          const isMissing = value === undefined || value === null;
          missingFields.push(sectionKey);
          if (isMissing) {
            missingValues.push(sectionKey);
          } else {
            emptyValues.push(sectionKey);
          }
        }
      }
      continue;
    }

    // Plain nested object section
    validateNestedFields(
      sectionSchema as FieldSchema,
      sectionKey,
      data?.[sectionKey],
      missingFields,
      missingValues,
      emptyValues,
    );
  }

  return {
    isValid: missingFields.length === 0,
    missingFields,
    missingValues,
    emptyValues,
  };
};

/**
 * Check if a field path is in the missing fields list
 */
export const isFieldMissing = (
  fieldPath: string,
  missingFields: string[],
): boolean => {
  return missingFields.includes(fieldPath);
};

/**
 * Generate validation dictionary from missing fields array
 * Converts schema paths to component section keys and field names
 */
export const generateValidationDict = (
  missingFields: string[], // Array of schema paths like ["hero_section.header", "our_services.header"] or top-level ["color", "hero_section"]
  schema: FieldSchema | null,
): ValidationDict => {
  const validationDict: ValidationDict = {};

  for (const schemaPath of missingFields) {
    // Check if this is a top-level section name (no dots) or a field path (has dots)
    const isTopLevelSection = !schemaPath.includes(".");

    if (isTopLevelSection) {
      // This is a top-level section name (e.g., "color", "hero_section", "our_services")
      // Map the schema ID to component section
      const componentSection = getComponentSectionFromSchemaId(schemaPath);

      if (!componentSection) {
        continue;
      }

      // Initialize section if needed
      if (!validationDict[componentSection]) {
        validationDict[componentSection] = {};
      }

      // Find all fields that belong to this section from the mapping
      // This ensures we mark all known fields for the section, not just required ones
      const sectionMappings = SCHEMA_TO_COMPONENT_MAPPING.filter(
        (m) => m.schemaId === schemaPath,
      );

      if (sectionMappings.length > 0) {
        // Mark all known fields for this section as invalid
        for (const mapping of sectionMappings) {
          if (mapping.componentSection === componentSection) {
            validationDict[componentSection][mapping.componentField] = true;
          }
        }
      } else {
        // If no mappings found, try to find fields from schema
        if (schema) {
          const fields = parseSchemaFields(schema);
          const sectionFields = fields.filter((f) =>
            f.path.startsWith(schemaPath + "."),
          );

          // Mark all fields in this section as invalid
          for (const field of sectionFields) {
            const fieldMapping = schemaPathToComponentField(field.path);
            if (fieldMapping && fieldMapping.section === componentSection) {
              validationDict[componentSection][fieldMapping.field] = true;
            }
          }

          // If still no fields found, mark the section itself
          if (sectionFields.length === 0) {
            validationDict[componentSection]._section = true;
          }
        } else {
          // No schema available, mark the section itself
          validationDict[componentSection]._section = true;
        }
      }
    } else {
      // This is a field-level path (e.g., "hero_section.header", "our_services.header")
      const mapping = schemaPathToComponentField(schemaPath);

      if (!mapping) {
        // Try to still add it to validation dict using schema ID
        const sectionKey = schemaPath.split(".")[0];
        const componentSection = getComponentSectionFromSchemaId(sectionKey);
        if (componentSection) {
          if (!validationDict[componentSection]) {
            validationDict[componentSection] = {};
          }
          const fieldName = schemaPath.split(".").pop() || "_section";
          validationDict[componentSection][fieldName] = true;
        }
        continue;
      }

      const { section, field } = mapping;

      // Initialize section if needed
      if (!validationDict[section]) {
        validationDict[section] = {};
      }

      // Mark field as invalid (true = missing/empty)
      validationDict[section][field] = true;
    }
  }

  return validationDict;
};

// ============================================================================
// UI VALIDATION HELPERS
// ============================================================================

/**
 * Standard helper to check if a field has a validation error
 * Checks both field-level errors and section-level errors
 * If a value is provided and not empty, returns false even if validation dict says it's invalid
 *
 * @param fieldName - The component field name (e.g., "title", "items")
 * @param requiredValidation - The validation dictionary from validationDict[section]
 * @param value - Optional field value to check. If provided and not empty, error is cleared
 * @returns true if field has validation error
 */
export const hasFieldError = (
  fieldName: string,
  requiredValidation: { [field: string]: boolean } = {},
  value?: any,
): boolean => {
  // If value is provided and not empty, don't show error
  // This check happens FIRST to override any stale validation dict entries
  if (value !== undefined && value !== null) {
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed !== "") {
        return false; // Field has a non-empty value, no error
      }
    } else if (Array.isArray(value)) {
      if (value.length > 0) {
        return false; // Array has items, no error
      }
    } else {
      // Non-string, non-array value exists (boolean, number, object)
      return false; // Value exists, no error
    }
  }

  // Only show error if validation dict says so AND value is actually empty/missing
  const fieldError = requiredValidation[fieldName] === true;
  const sectionError = requiredValidation._section === true;
  return fieldError || sectionError;
};

/**
 * Standard helper to get error message for a field
 * Returns "Required" if field has error, otherwise returns custom error message
 * If a value is provided and not empty, returns undefined even if validation dict says it's invalid
 */
export const getFieldErrorMessage = (
  fieldName: string,
  requiredValidation: { [field: string]: boolean } = {},
  customErrors: Record<string, string> = {},
  value?: any,
): string | undefined => {
  if (customErrors[fieldName]) {
    return customErrors[fieldName];
  }
  if (hasFieldError(fieldName, requiredValidation, value)) {
    return "Required";
  }
  return undefined;
};

/**
 * Standard helper to get input styles for a field with validation
 * Returns consistent red border styling when field has error
 * If a value is provided and not empty, returns undefined even if validation dict says it's invalid
 *
 * @param fieldName - The component field name
 * @param requiredValidation - The validation dictionary
 * @param missingFields - Array of missing schema paths (optional, for backward compatibility)
 * @param schemaPath - Optional schema path to check in missingFields (for backward compatibility)
 * @param value - Optional field value to check. If provided and not empty, error is cleared
 * @returns Input styles object with red border if error exists
 */
export const getInputStyles = (
  fieldName: string,
  requiredValidation: { [field: string]: boolean } = {},
  missingFields: string[] = [],
  schemaPath?: string, // Optional schema path to check in missingFields (for backward compatibility)
  value?: any, // Optional field value to check
): { input: { borderColor?: string; borderWidth?: string } } | undefined => {
  // If value is provided and not empty, don't show error from missingFields either
  const hasValue =
    value !== undefined &&
    value !== null &&
    ((typeof value === "string" && value.trim() !== "") ||
      (Array.isArray(value) && value.length > 0) ||
      (typeof value !== "string" && !Array.isArray(value)));

  const hasError =
    hasFieldError(fieldName, requiredValidation, value) ||
    (!hasValue && schemaPath && missingFields.includes(schemaPath));

  if (!hasError) {
    return undefined;
  }

  return {
    input: {
      borderColor: "red",
      borderWidth: "2px",
    },
  };
};

/**
 * Standard helper to get label styles for a field with validation
 * Returns red color when field has error
 * If a value is provided and not empty, returns undefined even if validation dict says it's invalid
 */
export const getLabelStyles = (
  fieldName: string,
  requiredValidation: { [field: string]: boolean } = {},
  value?: any,
): { color?: string } => {
  return {
    color: hasFieldError(fieldName, requiredValidation, value)
      ? "red"
      : undefined,
  };
};

/**
 * Standard helper to format label text with asterisk for required fields
 * If a value is provided and not empty, returns label without asterisk even if validation dict says it's invalid
 */
export const getLabelText = (
  label: string,
  fieldName: string,
  requiredValidation: { [field: string]: boolean } = {},
  value?: any,
): string => {
  const hasError = hasFieldError(fieldName, requiredValidation, value);
  return hasError ? `${label} *` : label;
};
